import { useState, useEffect } from 'react';
import { Trophy, MessageSquare, ArrowRight, Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

interface PointsEarnedProps {
  pointsBase: number;
  originalScore?: number;
  improvedScore: number;
  currentScore?: number;
  pointsBonus: number;
  isChat: boolean;
  isRescoring?: boolean;
  isRemix?: boolean;
  improvements?: string[];
  showScoreTooltip?: boolean;
}

const TOOLTIP_STORAGE_KEY = 'cosmo_score_tooltip_seen';

export function PointsEarned({ 
  pointsBase, 
  originalScore,
  improvedScore,
  currentScore,
  pointsBonus, 
  isChat,
  isRescoring,
  isRemix,
  improvements,
  showScoreTooltip
}: PointsEarnedProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (!showScoreTooltip || localStorage.getItem(TOOLTIP_STORAGE_KEY)) return;
    const timer = setTimeout(() => {
      setTooltipOpen(true);
    }, 600);
    return () => clearTimeout(timer);
  }, [showScoreTooltip]);

  const handleDismissTooltip = () => {
    localStorage.setItem(TOOLTIP_STORAGE_KEY, 'true');
    setTooltipOpen(false);
  };
  const displayScore = currentScore ?? improvedScore;
  const hasOriginalScore = originalScore !== undefined;
  const improvement = hasOriginalScore ? displayScore - originalScore : 0;
  const pointsTotal = pointsBase + displayScore + pointsBonus;
  
  return (
    <Card className="border-primary/20 bg-card/80 backdrop-blur overflow-visible">
      <CardHeader className="pb-1">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="h-5 w-5 text-primary" />
          Prompt Score
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Popover open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <PopoverTrigger asChild>
            {hasOriginalScore ? (
              /* Before/After Comparison - only shown when we have real original score */
              <div className="flex items-center justify-between gap-2 sm:gap-4 cursor-default">
                {/* Original Score */}
                <div className="text-center flex-1 p-3 rounded-xl bg-muted/30 border border-border/50">
                  <p className="text-base text-muted-foreground mb-1">
                    {isRemix ? 'Original Prompt' : 'Original'}
                  </p>
                  <p className="text-2xl font-display font-bold text-muted-foreground">
                    {originalScore}
                  </p>
                </div>
                
                {/* Arrow + Improvement */}
                <div className="flex flex-col items-center shrink-0">
                  <ArrowRight className="w-5 h-5 text-success" />
                  <span className={`text-xs font-medium ${improvement >= 0 ? 'text-success' : 'text-destructive'}`}>
                    {improvement >= 0 ? `+${improvement}` : improvement}
                  </span>
                </div>
                
                {/* Improved/Current Score */}
                <div className="text-center flex-1 p-3 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                  <p className="text-base text-muted-foreground mb-1">
                    {isRemix ? 'Your Remix' : (currentScore !== undefined ? 'Current' : 'Improved')}
                  </p>
                  <p className="text-2xl font-display font-bold text-primary">
                    {isRescoring ? (
                      <span className="animate-pulse">...</span>
                    ) : (
                      displayScore
                    )}
                  </p>
                </div>
              </div>
            ) : (
              /* Single score display when no original score available */
              <div className="text-center p-4 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 cursor-default">
                <p className="text-base text-muted-foreground mb-1">
                  {currentScore !== undefined ? 'Current Score' : 'Improved Score'}
                </p>
                <p className="text-3xl font-display font-bold text-primary">
                  {isRescoring ? (
                    <span className="animate-pulse">...</span>
                  ) : (
                    displayScore
                  )}
                </p>
              </div>
            )}
          </PopoverTrigger>
          <PopoverContent 
            className="w-80 text-sm bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 shadow-[0_0_15px_rgba(218,165,67,0.15)]" 
            side="bottom" 
            align="start" 
            alignOffset={-48}
            sideOffset={8}
            collisionPadding={0}
          >
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="h-4 w-4 text-primary" />
              <span className="font-semibold text-foreground">Cosmo Scoring</span>
            </div>
            <p className="text-foreground mb-2">
              Scores are calculated by how clear, specific and effective your prompt is. Cosmo evaluates your original prompt and then improves it so you get the best AI results.
            </p>
            <p className="text-foreground mb-3">
              The improved prompt is ready for use or you can make changes right there in the canvas.
            </p>
            <Button size="sm" variant="default" onClick={handleDismissTooltip} className="w-full">
              Got it
            </Button>
          </PopoverContent>
        </Popover>

        <div className="pt-2 border-t border-border/50">
          <p className="text-sm font-medium text-foreground mb-2">Cosmo improved your prompt</p>
          {improvements && improvements.length > 0 && (
            <ul className="space-y-1">
              {improvements.map((imp, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  {imp}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
